import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { FunnelStep, getSessionId, getSessionIdSync } from '../lib/supabase';
import { saveFormStep, getSessionData } from '../services/formSubmissionService';
import { createLead } from '../services/leadsService';
import { logger } from '../lib/utils';
import { toast } from 'sonner';

// Form data structure
export interface FormData {
  gender: string;
  firstName: string;
  lastName: string;
  country: string;
  city: string;
  postalCode: string;
  knownInfo: string;
  street: string;
  houseNumber: string;
  birthdate: string;
  email: string;
  paymentMethod: string;
  agreesToTerms: boolean;
  agreesToNoMarketing: boolean;
  orderNumber?: string; // Added for storing order number after lead creation
}

// Form context structure
interface FormContextType {
  formData: FormData;
  updateFormData: <K extends keyof FormData>(field: K, value: FormData[K]) => void;
  resetForm: () => void;
  currentStep: FunnelStep;
  setCurrentStep: (step: FunnelStep) => void;
  isLoading: boolean;
  nextStep: (data: Partial<FormData>) => Promise<void>;
  isSaving: boolean;
  isRestoringData: boolean; // New: indicates if data is being restored from storage/database
}

// Initial form data
const initialFormData: FormData = {
  gender: '',
  firstName: '',
  lastName: '',
  country: 'Deutschland',
  city: '',
  postalCode: '',
  knownInfo: '',
  street: '',
  houseNumber: '',
  birthdate: '',
  email: '',
  paymentMethod: '',
  agreesToTerms: false,
  agreesToNoMarketing: false,
  orderNumber: '',
};

// LocalStorage key for form data persistence
const FORM_DATA_STORAGE_KEY = 'adressfinder_form_data';

// Helper function to map URL pathname to FunnelStep
const pathToStep = (pathname: string): FunnelStep => {
  // Remove leading slash and convert to step name
  const path = pathname.replace(/^\//, '');
  
  const pathMap: Record<string, FunnelStep> = {
    'personal-data': 'personal_data',
    'address-data': 'address_data',
    'known-info': 'known_info',
    'address-details': 'address_details',
    'birthdate': 'birthdate',
    'both-info': 'both_info',
    'no-info': 'no_info',
    'email': 'email',
    'loading': 'loading',
    'payment': 'payment',
    'success': 'success',
  };
  
  return pathMap[path] || 'personal_data';
};

// Helper function to save form data to localStorage
const saveToLocalStorage = (data: FormData) => {
  try {
    localStorage.setItem(FORM_DATA_STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    logger.error('Error saving to localStorage:', error);
  }
};

// Helper function to load form data from localStorage
const loadFromLocalStorage = (): FormData | null => {
  try {
    const stored = localStorage.getItem(FORM_DATA_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Merge with initialFormData to ensure all fields exist
      return { ...initialFormData, ...parsed };
    }
  } catch (error) {
    logger.error('Error loading from localStorage:', error);
  }
  return null;
};

// Create context
const FormContext = createContext<FormContextType | undefined>(undefined);

// Context provider component
export const FormProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const location = useLocation();
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [currentStep, setCurrentStep] = useState<FunnelStep>('personal_data');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isRestoringData, setIsRestoringData] = useState(true); // Start as true, will be set to false once data is restored
  
  // Synchronize currentStep with URL pathname
  useEffect(() => {
    const step = pathToStep(location.pathname);
    setCurrentStep(step);
    logger.log(`Current step updated from URL: ${step} (${location.pathname})`);
  }, [location.pathname]);
  
  // Initialize session ID and restore form data when component mounts
  useEffect(() => {
    const initSession = async () => {
      try {
        const sid = await getSessionId();
        setSessionId(sid);
        logger.log('Session initialized:', sid);

        // First, try to restore from localStorage (fast)
        const localData = loadFromLocalStorage();
        if (localData) {
          logger.log('Restored form data from localStorage');
          setFormData(localData);
        }

        // Then, try to fetch the latest data from database (if available)
        try {
          const result = await getSessionData(sid);
          if (result.success && result.data) {
            const { form_steps, lead } = result.data;
            
            logger.log('Database query result:', { 
              form_steps_count: form_steps?.length || 0, 
              has_lead: !!lead 
            });
            
            // Reconstruct form data from all form steps
            let reconstructedData: Partial<FormData> = {};
            
            if (form_steps && form_steps.length > 0) {
              // Merge all form steps data (later steps override earlier ones)
              form_steps.forEach((step: any) => {
                // The database column is 'data', not 'form_data'
                const stepData = step.data || step.form_data;
                if (stepData) {
                  logger.log('Merging data from step:', step.step, stepData);
                  reconstructedData = { ...reconstructedData, ...stepData };
                }
              });
            }
            
            // Also include lead data if available
            if (lead) {
              if (lead.order_number) reconstructedData.orderNumber = lead.order_number;
              if (lead.email) reconstructedData.email = lead.email;
            }
            
            // If we have any reconstructed data, update formData
            if (Object.keys(reconstructedData).length > 0) {
              const mergedData = { ...initialFormData, ...reconstructedData };
              logger.log('Restored form data from database:', mergedData);
              setFormData(mergedData);
              saveToLocalStorage(mergedData); // Update localStorage with latest from DB
            } else {
              logger.warn('No form data found in database');
              
              // This is a NEW session with no data in the database yet
              // Clear the order number if one exists in localStorage from a previous session
              // This ensures each new session can create its own lead
              if (localData && localData.orderNumber) {
                logger.log('Clearing order number from previous session for new session:', sid);
                const cleanedData = { ...localData, orderNumber: '' };
                setFormData(cleanedData);
                saveToLocalStorage(cleanedData);
              }
            }
          }
        } catch (error) {
          logger.error('Error fetching session data from database:', error);
          // Continue with localStorage data if available
        }
      } finally {
        // Always set isRestoringData to false once we're done (success or failure)
        setIsRestoringData(false);
      }
    };
    
    initSession();
  }, []);

  /**
   * Navigate to next step with non-blocking save
   * Flow: Validate → Navigate immediately → Save in background
   */
  const nextStep = async (data: Partial<FormData>) => {
    try {
      setIsLoading(true);
      
      // Get session ID
      const sid = sessionId || getSessionIdSync();
      
      // Update form data immediately
      const updatedData = { ...formData, ...data };
      setFormData(updatedData);
      saveToLocalStorage(updatedData); // Persist to localStorage

      // Determine next step
      let nextStepName: FunnelStep = currentStep;
      switch (currentStep) {
        case 'personal_data':
          nextStepName = 'address_data';
          break;
        case 'address_data':
          nextStepName = 'known_info';
          break;
        case 'known_info':
          // Determine next step based on selection
          if (updatedData.knownInfo === 'address') {
            nextStepName = 'address_details';
          } else if (updatedData.knownInfo === 'birthdate') {
            nextStepName = 'birthdate';
          } else if (updatedData.knownInfo === 'both') {
            nextStepName = 'both_info';
          } else {
            nextStepName = 'no_info';
          }
          break;
        case 'address_details':
        case 'birthdate':
        case 'both_info':
        case 'no_info':
          nextStepName = 'email';
          break;
        case 'email':
          nextStepName = 'loading';
          break;
        case 'loading':
          // Lead will be created on payment page
          nextStepName = 'payment';
          break;
        case 'payment':
          nextStepName = 'success';
          break;
      }

      // Navigate immediately (non-blocking)
      setCurrentStep(nextStepName);
      setIsLoading(false);

      // Save form data in background (don't wait for it)
      setIsSaving(true);
      saveFormStep(sid, currentStep, data, true)
        .then(result => {
          if (result.success) {
            logger.log(`Step '${currentStep}' saved in background`);
          } else {
            logger.warn(`Background save for step '${currentStep}' queued for retry`);
          }
        })
        .catch(error => {
          logger.error(`Background save error for step '${currentStep}':`, error);
        })
        .finally(() => {
          setIsSaving(false);
        });

    } catch (error) {
      logger.error('Error in nextStep:', error);
      toast.error('Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
      setIsLoading(false);
    }
  };

  const updateFormData = <K extends keyof FormData>(field: K, value: FormData[K]) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };
      saveToLocalStorage(updated); // Persist to localStorage
      return updated;
    });
  };

  const resetForm = () => {
    setFormData(initialFormData);
    setCurrentStep('personal_data');
    localStorage.removeItem(FORM_DATA_STORAGE_KEY); // Clear localStorage
  };

  return (
    <FormContext.Provider
      value={{
        formData,
        updateFormData,
        resetForm,
        currentStep,
        setCurrentStep,
        isLoading,
        nextStep,
        isSaving,
        isRestoringData,
      }}
    >
      {children}
    </FormContext.Provider>
  );
};

// Custom hook for using the form context
export const useFormContext = () => {
  const context = useContext(FormContext);
  if (context === undefined) {
    throw new Error('useFormContext must be used within a FormProvider');
  }
  return context;
};
